/* eslint-disable no-unused-vars */
import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import {
  X,
  Link2,
  Download,
  Check,
  Share2,
  QrCode,
  Calendar,
  MapPin,
  Tag,
  Users,
  Ticket,
  Store,
  Lightbulb,
} from "lucide-react";
import eventPlaceholder from "../../../assets/images/no_image_placeholder.jpg";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const encodeShareText = (text) => encodeURIComponent(text);

// ─── Social icons ────────────────────────────────────────────────────────────

const XIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

// ─── Share targets ───────────────────────────────────────────────────────────

const buildShareTargets = (url, event) => {
  const headline = `Now Accepting Vendors — ${event?.eventName || "Our Event"}!`;
  const body = `Apply to be a marketplace vendor at ${event?.eventName || "this event"} in ${
    event?.city ? `${event.city}, ${event.state || ""}`.trim().replace(/,$/, "") : (event?.location || "")
  }. Spots are limited — apply today!`;
  const shortText = `${headline} ${url}`;

  return [
    {
      id: "x",
      label: "X / Twitter",
      color: "bg-black hover:bg-gray-800",
      icon: <XIcon />,
      href: `https://twitter.com/intent/tweet?text=${encodeShareText(shortText)}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      color: "bg-[#1877F2] hover:bg-[#166fe5]",
      icon: <FacebookIcon />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeShareText(url)}&quote=${encodeShareText(headline)}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      color: "bg-[#25D366] hover:bg-[#20bd5a]",
      icon: <WhatsAppIcon />,
      href: `https://wa.me/?text=${encodeShareText(shortText)}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      color: "bg-[#0A66C2] hover:bg-[#095db2]",
      icon: <LinkedInIcon />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeShareText(url)}&summary=${encodeShareText(body)}`,
    },
  ];
};

const buildAttendeeShareTargets = (url, event) => {
  const headline = `${event?.eventName || "Event"} — Get Your Tickets!`;
  const body = `Join us at ${event?.eventName || "this event"}${
    event?.city ? ` in ${event.city}${event.state ? `, ${event.state}` : ""}` : ""
  }. Grab your tickets before they sell out!`;
  const shortText = `${headline} ${url}`;

  return [
    {
      id: "x",
      label: "X / Twitter",
      color: "bg-black hover:bg-gray-800",
      icon: <XIcon />,
      href: `https://twitter.com/intent/tweet?text=${encodeShareText(shortText)}`,
    },
    {
      id: "facebook",
      label: "Facebook",
      color: "bg-[#1877F2] hover:bg-[#166fe5]",
      icon: <FacebookIcon />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeShareText(url)}&quote=${encodeShareText(headline)}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      color: "bg-[#25D366] hover:bg-[#20bd5a]",
      icon: <WhatsAppIcon />,
      href: `https://wa.me/?text=${encodeShareText(shortText)}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      color: "bg-[#0A66C2] hover:bg-[#095db2]",
      icon: <LinkedInIcon />,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeShareText(url)}&summary=${encodeShareText(body)}`,
    },
  ];
};

// ─── Event Preview Card ───────────────────────────────────────────────────────

const EventPreviewCard = React.forwardRef(({ event, url, audience = "vendor" }, ref) => {
  const coverPhoto = event?.eventCoverPhoto || event?.coverPhoto;
  const location =
    event?.city
      ? `${event.city}${event.state ? `, ${event.state}` : ""}`
      : event?.location || "";
  const isVendor = audience === "vendor";

  return (
    <div
      ref={ref}
      className="relative w-full rounded-xl overflow-hidden bg-white border border-gray-200"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Cover image */}
      <div className="relative h-36 bg-gray-100 overflow-hidden">
        <img
          src={coverPhoto || eventPlaceholder}
          alt={event?.eventName}
          className="w-full h-full object-cover"
          crossOrigin="anonymous"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

        {/* Badge */}
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-600 text-white text-xs font-semibold rounded-md shadow-sm">
            {isVendor
              ? <><Store className="w-3 h-3" /> Accepting Vendors</>
              : <><Ticket className="w-3 h-3" /> Tickets Available</>
            }
          </span>
        </div>

        {/* Event name */}
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white font-bold text-base leading-snug line-clamp-2 drop-shadow">
            {event?.eventName || "Event Name"}
          </p>
        </div>
      </div>

      {/* Details */}
      <div className="px-4 py-3 space-y-1.5">
        {(event?.startDate || event?.endDate) && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span>
              {formatDate(event?.startDate)}
              {event?.endDate && event.endDate !== event.startDate
                ? ` – ${formatDate(event.endDate)}`
                : ""}
            </span>
          </div>
        )}
        {location && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        )}
        {isVendor && event?.registrationFee !== undefined && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span>
              Vendor fee:{" "}
              {event.registrationFee === 0 || !event.registrationFee
                ? "Free"
                : `$${event.registrationFee}`}
            </span>
          </div>
        )}
        {isVendor && event?.sellerTypes?.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {event.sellerTypes.slice(0, 3).map((type, i) => (
              <span key={i} className="px-2 py-0.5 bg-primary-25 text-primary-700 text-xs rounded font-medium">
                {type}
              </span>
            ))}
            {event.sellerTypes.length > 3 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-400 text-xs rounded">
                +{event.sellerTypes.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>

      {/* CTA footer */}
      <div className="px-4 py-2.5 bg-primary-25 border-t border-primary-50 flex items-center justify-between">
        <span className="text-xs text-gray-400 truncate max-w-[60%] font-mono">{url}</span>
        <span className="text-xs font-semibold text-primary-600 flex-shrink-0">
          {isVendor ? "Apply now" : "Get tickets"} &rarr;
        </span>
      </div>
    </div>
  );
});
EventPreviewCard.displayName = "EventPreviewCard";

// ─── Copy Button ─────────────────────────────────────────────────────────────

const CopyButton = ({ copied, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
      copied
        ? "bg-green-100 text-green-700"
        : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
    }`}
  >
    {copied ? <><Check className="w-3 h-3" />Copied</> : "Copy"}
  </button>
);

// ─── Main Modal ──────────────────────────────────────────────────────────────

const ShareEventModal = ({ isOpen, onClose, event, eventId }) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [activeView, setActiveView] = useState("share"); // "share" | "qr"
  const [audience, setAudience] = useState("vendor"); // "vendor" | "attendee"
  const [downloadingQR, setDownloadingQR] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState(false);
  const previewRef = useRef(null);
  const qrRef = useRef(null);

  const vendorSignupUrl = `${window.location.origin}/browse-events?eventId=${eventId}`;
  const attendeeUrl = `${window.location.origin}/events/${eventId}`;
  const activeUrl = audience === "vendor" ? vendorSignupUrl : attendeeUrl;
  const isVendor = audience === "vendor";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(activeUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch {
      /* silent */
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: isVendor
          ? `Apply to ${event?.eventName} as a Vendor`
          : event?.eventName || "Check out this event",
        text: isVendor
          ? `Now accepting marketplace vendors at ${event?.eventName}! Apply today.`
          : `Join us at ${event?.eventName}! Get your tickets now.`,
        url: activeUrl,
      });
    } catch {
      copyLink();
    }
  };

  const downloadQR = () => {
    if (!qrRef.current) return;
    setDownloadingQR(true);
    try {
      const canvas = qrRef.current.querySelector("canvas");
      if (!canvas) return;

      const size = canvas.width;
      const padding = 24;
      const labelHeight = 48;
      const out = document.createElement("canvas");
      out.width = size + padding * 2;
      out.height = size + padding * 2 + labelHeight;
      const ctx = out.getContext("2d");

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(canvas, padding, padding);

      ctx.fillStyle = "#111827";
      ctx.font = "bold 15px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(
        event?.eventName || (isVendor ? "Scan to apply" : "Scan for tickets"),
        out.width / 2,
        size + padding * 2 + 14
      );
      ctx.fillStyle = "#6B7280";
      ctx.font = "12px Inter, sans-serif";
      ctx.fillText(
        isVendor ? "Scan to apply as a vendor" : "Scan to get tickets",
        out.width / 2,
        size + padding * 2 + 34
      );

      const link = document.createElement("a");
      link.download = `${(event?.eventName || "event").replace(/\s+/g, "-").toLowerCase()}-${isVendor ? "vendor" : "attendee"}-qr.png`;
      link.href = out.toDataURL("image/png");
      link.click();
    } finally {
      setDownloadingQR(false);
    }
  };

  const downloadShareCard = async () => {
    if (!previewRef.current) return;
    setDownloadingCard(true);
    try {
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.download = `${(event?.eventName || "event").replace(/\s+/g, "-").toLowerCase()}-${isVendor ? "vendor" : "attendee"}-share-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Error generating share card:", err);
    } finally {
      setDownloadingCard(false);
    }
  };

  if (!isOpen) return null;

  const shareTargets = isVendor
    ? buildShareTargets(vendorSignupUrl, event)
    : buildAttendeeShareTargets(attendeeUrl, event);

  const tips = isVendor
    ? [
        "Print and display at your venue or table",
        "Add to vendor recruitment flyers",
        "Include in email outreach to past vendors",
        "Post on local business or maker communities",
      ]
    : [
        "Post on social media to drive ticket sales",
        "Add to event flyers and printed materials",
        "Send in your email newsletter",
        "Display at the venue entrance",
      ];

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl flex flex-col max-h-[92vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-50 rounded-xl flex items-center justify-center">
              <Share2 className="w-4 h-4 text-primary-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 leading-tight">Share Event</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {isVendor ? "Invite vendors to apply" : "Share with attendees for tickets"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Audience + view toggles */}
        <div className="px-5 pt-4 space-y-2 flex-shrink-0">
          {/* Audience */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
            <button
              onClick={() => { setAudience("vendor"); setCopiedLink(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-all ${
                isVendor
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Store className="w-3.5 h-3.5" />
              Vendors
            </button>
            <button
              onClick={() => { setAudience("attendee"); setCopiedLink(false); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-all ${
                !isVendor
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Ticket className="w-3.5 h-3.5" />
              Attendees
            </button>
          </div>

          {/* Share / QR view */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
            <button
              onClick={() => setActiveView("share")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-all ${
                activeView === "share"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Link2 className="w-3.5 h-3.5" />
              Share Link
            </button>
            <button
              onClick={() => setActiveView("qr")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-all ${
                activeView === "qr"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <QrCode className="w-3.5 h-3.5" />
              QR Code
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {activeView === "share" ? (
            <>
              {/* Preview card */}
              <EventPreviewCard ref={previewRef} event={event} url={activeUrl} audience={audience} />

              {/* Copy link */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-500 truncate flex-1 font-mono">{activeUrl}</span>
                <CopyButton copied={copiedLink} onClick={copyLink} />
              </div>

              {/* Social */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Share to</p>
                <div className="grid grid-cols-2 gap-2">
                  {shareTargets.map((target) => (
                    <a
                      key={target.id}
                      href={target.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 px-3 py-2.5 text-white text-sm font-medium rounded-xl transition ${target.color}`}
                    >
                      {target.icon}
                      {target.label}
                    </a>
                  ))}
                  {typeof navigator !== "undefined" && navigator.share && (
                    <button
                      onClick={nativeShare}
                      className="col-span-2 flex items-center gap-2 px-3 py-2.5 text-gray-700 text-sm font-medium rounded-xl bg-gray-100 hover:bg-gray-200 transition"
                    >
                      <Share2 className="w-4 h-4" />
                      More options
                    </button>
                  )}
                </div>
              </div>

              {/* Download */}
              <button
                onClick={downloadShareCard}
                disabled={downloadingCard}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {downloadingCard ? "Generating…" : "Download Share Image"}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <p className="text-sm text-gray-500 text-center">
                {isVendor
                  ? "Print or display this QR code so vendors can scan and apply instantly."
                  : "Print or display this QR code so attendees can scan and buy tickets."}
              </p>

              {/* QR code */}
              <div
                ref={qrRef}
                className="p-5 bg-white border border-gray-200 rounded-2xl flex flex-col items-center gap-3"
              >
                <QRCodeCanvas
                  value={activeUrl}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#111827"
                  level="H"
                  imageSettings={{
                    src: `${window.location.origin}/favicon.ico`,
                    x: undefined,
                    y: undefined,
                    height: 28,
                    width: 28,
                    excavate: true,
                  }}
                />
                <p className="text-xs font-semibold text-gray-800 text-center">
                  {event?.eventName || (isVendor ? "Scan to apply" : "Scan for tickets")}
                </p>
                <p className="text-xs text-gray-400 text-center">
                  {isVendor ? "Scan to apply as a marketplace vendor" : "Scan to get tickets"}
                </p>
              </div>

              {/* URL */}
              <div className="w-full flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-500 truncate flex-1 font-mono">{activeUrl}</span>
                <CopyButton copied={copiedLink} onClick={copyLink} />
              </div>

              <button
                onClick={downloadQR}
                disabled={downloadingQR}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {downloadingQR ? "Generating…" : "Download QR Code"}
              </button>

              {/* Tips */}
              <div className="w-full border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="w-3.5 h-3.5 text-primary-500" />
                  <p className="text-xs font-semibold text-gray-700">Tips for using this QR code</p>
                </div>
                <ul className="space-y-1.5">
                  {tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                      <span className="w-1 h-1 rounded-full bg-primary-400 mt-1.5 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareEventModal;
